import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { AppointmentsProps } from '../components/appointments/appointments.interface';
import { isAccessible } from '../components/appointments/Appointments';
import axios from 'axios';
import { AppDispatch } from '../redux/rootReducer';
import { useDispatch } from 'react-redux';
import { outSideGeoLocationAlert } from '../redux/appointment/actions/appointment.action';
import { toast } from 'react-toastify';
// import { useGeolocation } from '../hooks/geoLocation';
import LocationIdPage from '../pages/LocationIdPage';

export interface AlertObjectProps {
    appointmentId: string,
    tourLatitude: number,
    tourLongitude: number
}

const ProtectedRoutes = () => {
    const token: string | null = localStorage.getItem('visitorAccessToken');
    // const { coordinates, isEnabled, isSupported } = useGeolocation();
    const { search, pathname } = useLocation();
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const [currentAppointments, setCurrentAppointments] = useState<AppointmentsProps[]>([]);
    const dispatch: AppDispatch = useDispatch();

    // Check if the current path matches the location pattern: /location/{uuid}
    const isLocationPath = pathname.includes('/location');

    // Utility functions for geolocation and appointments
    const getDistanceFromLatLonInMeters = useCallback(async (lat1: number, lon1: number, lat2: number, lon2: number) => {
        const R = 6371000; // Radius of the Earth in meters
        const dLat = (lat2 - lat1) * (Math.PI / 180);
        const dLon = (lon2 - lon1) * (Math.PI / 180);

        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * (Math.PI / 180)) *
            Math.cos(lat2 * (Math.PI / 180)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c; // Distance in meters
        return distance;
    }, []);

    const isOutsideRadius = useCallback(async (userLat: number, userLon: number, propertyLat: number, propertyLon: number, radiusInMiles: number) => {
        const radiusInMeters = radiusInMiles * 1609.34;
        const distance = await getDistanceFromLatLonInMeters(userLat, userLon, propertyLat, propertyLon);
        return distance > radiusInMeters;
    }, [getDistanceFromLatLonInMeters]);

    // Stop tracking location
    const stopTracking = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const proceedAppointments = useCallback(() => {
        if (currentAppointments?.length) {
            const appointments: AppointmentsProps[] = [];
            currentAppointments?.forEach((appointment: AppointmentsProps) => {
                if (isAccessible(appointment?.appointmentDate, appointment?.appointmentStartTime, appointment?.appointmentEndTime) === 1) {
                    appointments.push(appointment);
                }
            });

            if (appointments !== currentAppointments && appointments?.length) {
                localStorage.setItem('currentAppointments', JSON.stringify(appointments))
            } else {
                localStorage.removeItem('currentAppointments');
                stopTracking();
            }
        }
    }, [currentAppointments, stopTracking]);

    const onOutSideAlertSuccess = useCallback(async () => {
        toast.success('alert sent');
        const otherAppointment = currentAppointments?.filter((appointment) => isAccessible(appointment?.appointmentDate, appointment?.appointmentStartTime, appointment?.appointmentEndTime) !== 1) || [];
        if (otherAppointment?.length > 0) {
            localStorage.setItem('currentAppointments', JSON.stringify(otherAppointment));
        } else {
            stopTracking();
            setCurrentAppointments([])
            localStorage.removeItem('currentAppointments')
        }
        stopTracking();
    }, [currentAppointments, stopTracking]);

    const startTracking = useCallback(async (appointment: AppointmentsProps) => {
        // Prevent multiple intervals for the same appointment
        if (intervalRef.current) {
            return;
        }

        intervalRef.current = setInterval(() => {
            if (navigator.geolocation && intervalRef?.current) {
                navigator.geolocation.getCurrentPosition(
                    async (position) => {
                        const { latitude, longitude } = position.coords;
                        const isOutSide = await isOutsideRadius(latitude, longitude, appointment?.latitude || 0, appointment?.longitude || 0, appointment?.radius || 0)

                        if (isOutSide) {
                            axios.get(`/appointments/${appointment?.appointmentId}`)
                                .then(response => {
                                    const { data } = response;
                                    const { appointment } = data;
                                    if (appointment && appointment.alertNotificationCount === 0 && appointment?.entryExitCount % 2 !== 0) {
                                        const alertObject: AlertObjectProps = {
                                            appointmentId: appointment?.appointmentId,
                                            tourLatitude: latitude,
                                            tourLongitude: longitude
                                        }
                                        dispatch(outSideGeoLocationAlert(alertObject, onOutSideAlertSuccess))
                                    } else if (appointment && appointment?.alertNotificationCount > 0) {
                                        const otherAppointment = currentAppointments?.filter((oldAppointment) => oldAppointment?.appointmentId !== appointment.appointmentId) || [];
                                        if (otherAppointment?.length > 0) {
                                            localStorage.setItem('currentAppointments', JSON.stringify(otherAppointment));
                                        } else {
                                            stopTracking();
                                            setCurrentAppointments([])
                                            localStorage.removeItem('currentAppointments')
                                        }
                                    } else if (isAccessible(appointment?.appointmentDate, appointment?.appointmentStartTime, appointment?.appointmentEndTime) !== 1) {
                                        const otherAppointment = currentAppointments?.filter((appointment) => isAccessible(appointment?.appointmentDate, appointment?.appointmentStartTime, appointment?.appointmentEndTime) !== 1) || [];
                                        if (otherAppointment?.length > 0) {
                                            localStorage.setItem('currentAppointments', JSON.stringify(otherAppointment));
                                        } else {
                                            stopTracking();
                                            setCurrentAppointments([]);
                                            localStorage.removeItem('currentAppointments')
                                        }
                                    }
                                }).catch(error => console.error('error: ', error))
                        }
                    }, (error) => {
                        console.error(error)
                    },
                    { enableHighAccuracy: true }
                );
            }
        }, 10000); // Check location every 10 seconds
    }, [isOutsideRadius, currentAppointments, dispatch, onOutSideAlertSuccess, stopTracking]);

    const handleStorageUpdate = useCallback(() => {
        const newValue = localStorage.getItem("currentAppointments");
        if (newValue && JSON.parse(newValue) !== currentAppointments) {
            setCurrentAppointments(JSON.parse(newValue));
        }
    }, [currentAppointments]);

    // Scroll to top on route change
    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth',
        });
    }, [pathname]);

    // Initialize appointments from localStorage
    useEffect(() => {
        window.addEventListener('localStorageUpdated', handleStorageUpdate);

        const newValue = localStorage.getItem("currentAppointments");
        if (newValue && JSON.parse(newValue) !== currentAppointments) {
            setCurrentAppointments(JSON.parse(newValue));
        }

        return () => {
            window.removeEventListener('localStorageUpdated', handleStorageUpdate);
        };
    }, [handleStorageUpdate]);

    // Process appointments when they change
    useEffect(() => {
        if (currentAppointments) {
            proceedAppointments();
        }
    }, [proceedAppointments]);

    // Start tracking for valid appointments
    useEffect(() => {
        if (currentAppointments?.length) {
            currentAppointments.forEach((appointment: AppointmentsProps) => {
                const appointmentStatus = isAccessible(appointment?.appointmentDate, appointment?.appointmentStartTime, appointment?.appointmentEndTime);
                if (
                    appointmentStatus === 1 &&
                    appointment.alertNotificationCount === 0 &&
                    appointment?.latitude &&
                    appointment?.longitude
                ) {
                    startTracking(appointment);
                }
            })
        }
    }, [currentAppointments, startTracking]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopTracking();
        };
    }, [stopTracking]);

    // Main authentication logic
    // If user is not logged in, redirect to LocationIdPage for all routes
    if (!token && isLocationPath) {
        return <LocationIdPage />;
    }

    if (!token && !isLocationPath) {
        return <Navigate to="/signup" replace state={{ destinationURL: pathname, modifiedAppointment: search }} />
    }

    // If user is logged in, render the protected content
    return (
        <div>
            {/* <div style={{ position: 'fixed', zIndex: '9999', padding: '16px', bottom: '0', justifyContent: 'center', backgroundColor: 'black', width: '100%', display: 'flex', alignItems: 'center', gap: '16px', fontSize: 14 }}>
                {(isSupported && isEnabled) ? <>
                    <span style={{ color: 'white' }}>{`Lat: ${coordinates?.latitude}`}</span>
                    <span style={{ color: 'white' }}>{`Long: ${coordinates?.longitude}`}</span>
                </>
                    : !isSupported ? <span style={{ color: 'white' }}>Not Supported</span> : <span style={{ color: 'white' }}>Permission Not Enabled</span>}
            </div> */}
            <Outlet />
        </div>
    );
};

export default ProtectedRoutes;
